import React from "react";
import { Head, usePage, useForm } from "@inertiajs/react";
import styled from "styled-components";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import PortalApp from "../../portal/App";
import {
  PortalContainer,
  PortalSection,
  PortalCard,
  PortalCardTitle,
} from "../../portal/PortalCard";
import {
  brand_orange_deep,
  brand_gradient,
  narrowScreenMediaQuery,
} from "../../constants/styles";

export default function Profile() {
  const { profile = {}, endpoints = {}, meta = {} } = usePage().props;

  return (
    <>
      <Head>
        <title>Profil — Taxsam Learning Center</title>
        <meta name="description" content="Kelola profil pribadi, pekerjaan, dan keamanan akun Taxsam Learning Center." />
      </Head>
      <PortalContainer>
        <SectionErrorBoundary name="ProfilePribadi">
          <PersonalSection profile={profile} endpoints={endpoints} meta={meta} />
        </SectionErrorBoundary>
        <SectionErrorBoundary name="ProfilePekerjaan">
          <WorkSection profile={profile} endpoints={endpoints} meta={meta} />
        </SectionErrorBoundary>
        <SectionErrorBoundary name="ProfileKeamanan">
          <SecuritySection profile={profile} endpoints={endpoints} />
        </SectionErrorBoundary>
      </PortalContainer>
    </>
  );
}

Profile.layout = (page) => <PortalApp>{page}</PortalApp>;

// --- Sections ---------------------------------------------------------------

function PersonalSection({ profile, endpoints, meta }) {
  const form = useForm({
    user: {
      full_name:    profile.full_name    || "",
      phone_number: profile.phone_number || "",
      religion:     profile.religion     || "",
      nik:          profile.nik          || "",
      kk_number:    profile.kk_number    || "",
      birthday:     profile.birthday     || "",
      gender:       profile.gender       || "",
      address:      profile.address      || "",
      province:     profile.province     || "",
      city:         profile.city         || "",
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!endpoints.update_url) return;
    form.transform((data) => ({ ...data, _section: "personal" }));
    form.patch(endpoints.update_url, {
      preserveScroll: true,
      preserveState: true,
    });
  };

  return (
    <PortalSection
      title="Profil Pribadi"
      subtitle="Pastikan nama lengkap sesuai KTP — digunakan pada penamaan sertifikat."
    >
      <PortalCard as="form" onSubmit={handleSubmit}>
        <Grid>
          <Field>
            <Label htmlFor="profile-full-name">Nama Lengkap</Label>
            <Input
              id="profile-full-name"
              name="full_name"
              type="text"
              value={form.data.user.full_name}
              onChange={(e) => form.setData("user", { ...form.data.user, full_name: e.target.value })}
              autoComplete="name"
              required
            />
            {form.errors["user.full_name"] && <ErrorText>{form.errors["user.full_name"]}</ErrorText>}
          </Field>
          <Field>
            <Label htmlFor="profile-email">Email</Label>
            <Input
              id="profile-email"
              name="email"
              type="email"
              autoComplete="email"
              value={profile.email || ""}
              readOnly
              aria-readonly="true"
            />
            <Hint>Email tidak dapat diubah dari halaman ini.</Hint>
          </Field>
          <Field>
            <Label htmlFor="profile-phone">Nomor Handphone</Label>
            <Input
              id="profile-phone"
              name="phone_number"
              type="tel"
              value={form.data.user.phone_number}
              onChange={(e) => form.setData("user", { ...form.data.user, phone_number: e.target.value })}
              autoComplete="tel"
              inputMode="tel"
            />
          </Field>
          <Field>
            <Label htmlFor="profile-religion">Agama</Label>
            <Select
              id="profile-religion"
              name="religion"
              value={form.data.user.religion}
              onChange={(e) => form.setData("user", { ...form.data.user, religion: e.target.value })}
            >
              <option value="">Pilih Agama</option>
              {(meta.religion_options || []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label htmlFor="profile-nik">NIK</Label>
            <Input
              id="profile-nik"
              name="nik"
              type="text"
              autoComplete="off"
              value={form.data.user.nik}
              onChange={(e) => form.setData("user", { ...form.data.user, nik: e.target.value })}
              inputMode="numeric"
              pattern="\d{16}"
              maxLength={16}
            />
          </Field>
          <Field>
            <Label htmlFor="profile-kk">Nomor Kartu Keluarga</Label>
            <Input
              id="profile-kk"
              name="kk_number"
              type="text"
              autoComplete="off"
              value={form.data.user.kk_number}
              onChange={(e) => form.setData("user", { ...form.data.user, kk_number: e.target.value })}
              inputMode="numeric"
              maxLength={16}
            />
          </Field>
          <Field>
            <Label htmlFor="profile-birthday">Tanggal Lahir</Label>
            <Input
              id="profile-birthday"
              name="birthday"
              type="date"
              autoComplete="bday"
              value={(form.data.user.birthday || "").slice(0, 10)}
              onChange={(e) => form.setData("user", { ...form.data.user, birthday: e.target.value })}
            />
          </Field>
          <Field>
            <Label htmlFor="profile-gender">Jenis Kelamin</Label>
            <Select
              id="profile-gender"
              name="gender"
              value={form.data.user.gender}
              onChange={(e) => form.setData("user", { ...form.data.user, gender: e.target.value })}
            >
              <option value="">Pilih Jenis Kelamin</option>
              {(meta.gender_options || []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </Field>
          <FieldFull>
            <Label htmlFor="profile-address">Alamat Domisili</Label>
            <Input
              id="profile-address"
              name="address"
              type="text"
              value={form.data.user.address}
              onChange={(e) => form.setData("user", { ...form.data.user, address: e.target.value })}
              autoComplete="street-address"
            />
          </FieldFull>
          <Field>
            <Label htmlFor="profile-province">Provinsi</Label>
            <Input
              id="profile-province"
              name="province"
              type="text"
              autoComplete="address-level1"
              value={form.data.user.province}
              onChange={(e) => form.setData("user", { ...form.data.user, province: e.target.value })}
            />
          </Field>
          <Field>
            <Label htmlFor="profile-city">Kabupaten/Kota</Label>
            <Input
              id="profile-city"
              name="city"
              type="text"
              autoComplete="address-level2"
              value={form.data.user.city}
              onChange={(e) => form.setData("user", { ...form.data.user, city: e.target.value })}
            />
          </Field>
        </Grid>
        <Footer>
          <SubmitButton type="submit" disabled={form.processing}>
            {form.processing ? "Menyimpan…" : "Simpan Profil Pribadi"}
          </SubmitButton>
          {form.recentlySuccessful && <SuccessText>Tersimpan ✓</SuccessText>}
        </Footer>
      </PortalCard>
    </PortalSection>
  );
}

function WorkSection({ profile, endpoints, meta }) {
  const form = useForm({
    user: {
      position:       profile.position       || "",
      company:        profile.company        || "",
      business_field: profile.business_field || "",
      city_company:   profile.city_company   || "",
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!endpoints.update_url) return;
    form.patch(endpoints.update_url, {
      preserveScroll: true,
      preserveState: true,
    });
  };

  return (
    <PortalSection
      title="Profil Pekerjaan"
      subtitle="Bantu kami menyesuaikan rekomendasi materi sesuai latar belakang Anda."
    >
      <PortalCard as="form" onSubmit={handleSubmit}>
        <Grid>
          <Field>
            <Label htmlFor="work-position">Profesi</Label>
            <Select
              id="work-position"
              name="position"
              value={form.data.user.position}
              onChange={(e) => form.setData("user", { ...form.data.user, position: e.target.value })}
            >
              <option value="">Pilih Profesi</option>
              {(meta.position_options || []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label htmlFor="work-company">Nama Perusahaan / Institusi</Label>
            <Input
              id="work-company"
              name="company"
              type="text"
              autoComplete="organization"
              value={form.data.user.company}
              onChange={(e) => form.setData("user", { ...form.data.user, company: e.target.value })}
            />
          </Field>
          <Field>
            <Label htmlFor="work-business-field">Bidang Usaha / Jurusan</Label>
            <Input
              id="work-business-field"
              name="business_field"
              type="text"
              autoComplete="off"
              value={form.data.user.business_field}
              onChange={(e) => form.setData("user", { ...form.data.user, business_field: e.target.value })}
            />
          </Field>
          <Field>
            <Label htmlFor="work-city">Kabupaten/Kota Perusahaan</Label>
            <Input
              id="work-city"
              name="city_company"
              type="text"
              autoComplete="address-level2"
              value={form.data.user.city_company}
              onChange={(e) => form.setData("user", { ...form.data.user, city_company: e.target.value })}
            />
          </Field>
        </Grid>
        <Footer>
          <SubmitButton type="submit" disabled={form.processing}>
            {form.processing ? "Menyimpan…" : "Simpan Profil Pekerjaan"}
          </SubmitButton>
          {form.recentlySuccessful && <SuccessText>Tersimpan ✓</SuccessText>}
        </Footer>
      </PortalCard>
    </PortalSection>
  );
}

function SecuritySection({ profile, endpoints }) {
  const form = useForm({ user: { password: "", password_confirmation: "" } });
  const [showPassword, setShowPassword] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!endpoints.update_url) return;
    if (!form.data.user.password) return;
    if (form.data.user.password !== form.data.user.password_confirmation) {
      form.setError("user.password_confirmation", "Konfirmasi password tidak cocok");
      return;
    }
    form.patch(endpoints.update_url, {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    });
  };

  return (
    <PortalSection
      title="Keamanan Akun"
      subtitle="Gunakan kombinasi angka, huruf kapital, dan simbol minimal 8 karakter."
    >
      <PortalCard as="form" onSubmit={handleSubmit}>
        {/* Hidden username field for password-manager + a11y compliance.
            Browsers expect a username sibling to a password input so the
            password-manager can attach the credential to the right account. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={profile?.email || ""}
          readOnly
          aria-hidden="true"
          tabIndex={-1}
          style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}
        />
        <Grid>
          <Field>
            <Label htmlFor="security-password">Password Baru</Label>
            <PasswordWrap>
              <Input
                id="security-password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.data.user.password}
                onChange={(e) => form.setData("user", { ...form.data.user, password: e.target.value })}
                autoComplete="new-password"
                placeholder="Masukkan password baru"
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
              >
                {showPassword ? "Sembunyikan" : "Tampilkan"}
              </PasswordToggle>
            </PasswordWrap>
          </Field>
          <Field>
            <Label htmlFor="security-password-confirm">Ulangi Password Baru</Label>
            <Input
              id="security-password-confirm"
              name="password_confirmation"
              type={showPassword ? "text" : "password"}
              value={form.data.user.password_confirmation}
              onChange={(e) => form.setData("user", { ...form.data.user, password_confirmation: e.target.value })}
              autoComplete="new-password"
              placeholder="Masukkan ulang password baru"
            />
            {form.errors["user.password_confirmation"] && (
              <ErrorText>{form.errors["user.password_confirmation"]}</ErrorText>
            )}
          </Field>
        </Grid>
        <Footer>
          <SubmitButton
            type="submit"
            disabled={form.processing || !form.data.user.password}
          >
            {form.processing ? "Menyimpan…" : "Ubah Password"}
          </SubmitButton>
          {form.recentlySuccessful && <SuccessText>Password diperbarui ✓</SuccessText>}
        </Footer>
      </PortalCard>
    </PortalSection>
  );
}

// --- Styles -----------------------------------------------------------------

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 18px;

  @media ${narrowScreenMediaQuery} { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldFull = styled(Field)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #6b7280;

  [data-theme="dark"] & { color: #cbd5e1; }
`;

const Input = styled.input`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #1f2937;
  font-size: 14px;
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:focus {
    outline: none;
    border-color: ${brand_orange_deep};
    box-shadow: 0 0 0 3px rgba(216, 89, 7, 0.15);
  }

  &[readonly] {
    background: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
    color: #f3f4f6;
    &[readonly] { background: rgba(255, 255, 255, 0.03); color: #9ca3af; }
  }
`;

const Select = styled.select`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #1f2937;
  font-size: 14px;
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, ${brand_orange_deep} 50%),
                    linear-gradient(135deg, ${brand_orange_deep} 50%, transparent 50%);
  background-position: calc(100% - 18px) center, calc(100% - 13px) center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;

  &:focus {
    outline: none;
    border-color: ${brand_orange_deep};
    box-shadow: 0 0 0 3px rgba(216, 89, 7, 0.15);
  }

  [data-theme="dark"] & {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
    color: #f3f4f6;
  }
`;

const Hint = styled.span`
  font-size: 11px;
  color: #6b7280;

  [data-theme="dark"] & { color: #9ca3af; }
`;

const ErrorText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
  flex-wrap: wrap;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 12px 26px;
  border-radius: 999px;
  border: none;
  background: ${brand_gradient};
  color: #ffffff;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: filter 150ms ease, transform 100ms ease;

  &:hover:not(:disabled) { filter: brightness(0.97); transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
`;

const SuccessText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #047857;
`;

const PasswordWrap = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  font-size: 11px;
  font-weight: 700;
  color: ${brand_orange_deep};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;

  &:hover { background: rgba(216, 89, 7, 0.08); }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 1px; }
`;
